/**
 * Client-side nickname moderation (mirrors server). Server always re-validates on save.
 */

const LEET_MAP: Record<string, string> = {
  "@": "a",
  "4": "a",
  "8": "b",
  "(": "c",
  "<": "c",
  "3": "e",
  "6": "g",
  "#": "u",
  "1": "i",
  "!": "i",
  "|": "i",
  "0": "o",
  "5": "s",
  "$": "s",
  "7": "t",
  "+": "t",
  "2": "z",
};

const BLOCKED_TOKENS = new Set([
  "fuck",
  "fuk",
  "fck",
  "shit",
  "sht",
  "cunt",
  "cnt",
  "damn",
  "dick",
  "cock",
  "piss",
  "hell",
  "ass",
  "cum",
  "fag",
  "fgt",
  "jap",
  "kike",
  "kkk",
  "nazi",
  "rag",
  "slut",
  "sux",
  "tit",
  "wog",
  "boob",
  "coon",
  "dyke",
  "gook",
  "heil",
  "mong",
  "paki",
  "poof",
  "poon",
  "rape",
  "sex",
  "spic",
  "twat",
  "wang",
  "weed",
  "wetback",
  "chink",
  "nonce",
  "pedo",
  "puta",
  "pute",
  "merde",
  "salaud",
  "salop",
  "con",
  "connard",
  "connasse",
  "encule",
  "enfoire",
  "fdp",
  "ntm",
  "pd",
  "tg",
]);

const BLOCKED_COMPACT_PHRASES = [
  "fuck",
  "fuk",
  "fck",
  "shit",
  "sht",
  "bitch",
  "btch",
  "bastard",
  "cunt",
  "cnt",
  "pussy",
  "penis",
  "vagina",
  "whore",
  "slut",
  "nigger",
  "nigga",
  "niga",
  "faggot",
  "faget",
  "fagot",
  "retard",
  "rtard",
  "rapist",
  "molest",
  "pedoph",
  "paedo",
  "hitler",
  "holohoax",
  "gasjew",
  "killall",
  "rape",
  "incest",
  "putain",
  "foutre",
  "niquer",
  "nique",
  "salope",
  "enculer",
  "batard",
  "bordel",
  "suckmy",
  "suckdick",
  "blowjob",
  "handjob",
  "jackoff",
  "jerkoff",
  "asshole",
  "ashole",
  "bullshit",
  "shithead",
  "dumbass",
  "motherf",
];

const ALLOWED_COMPACT = new Set([
  "cockpit",
  "skyhawk",
  "airbus",
  "class",
  "classic",
  "assess",
  "asset",
  "assign",
  "assistant",
  "passenger",
]);

export function normalizeNicknameForModeration(raw: string): string {
  let s = raw.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
  let out = "";
  for (const ch of s) {
    out += LEET_MAP[ch] ?? ch;
  }
  return out;
}

function nicknameTokens(normalized: string): string[] {
  return normalized
    .split(/[\s_-]+/)
    .map((t) => t.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean);
}

function nicknameCompact(normalized: string): string {
  return normalized.replace(/[^a-z0-9]/g, "");
}

function compactIsAllowed(compact: string): boolean {
  return !compact || ALLOWED_COMPACT.has(compact);
}

function compactContainsBlockedPhrase(compact: string): boolean {
  if (!compact || compactIsAllowed(compact)) return false;
  for (const phrase of BLOCKED_COMPACT_PHRASES) {
    if (phrase.length < 5) continue;
    if (compact.includes(phrase)) return true;
  }
  return false;
}

function tokenIsBlocked(token: string): boolean {
  if (!token) return false;
  if (BLOCKED_TOKENS.has(token)) return true;
  if (token.length >= 5 && BLOCKED_COMPACT_PHRASES.some((p) => p.length >= 5 && token === p)) {
    return true;
  }
  return false;
}

export function validateNicknameModeration(trimmed: string): { ok: true } | { ok: false } {
  const normalized = normalizeNicknameForModeration(trimmed);
  const tokens = nicknameTokens(normalized);
  const compact = nicknameCompact(normalized);

  if (!compact) return { ok: false };

  for (const token of tokens) {
    if (tokenIsBlocked(token)) return { ok: false };
  }

  if (compactContainsBlockedPhrase(compact)) return { ok: false };

  if (/^(.)\1{5,}$/.test(compact)) return { ok: false };

  return { ok: true };
}

export const NICKNAME_NOT_ALLOWED_MESSAGE =
  "This nickname is not allowed. Please choose a respectful name without profanity or offensive language.";
